/**
 * RiskBreakdownPanel
 * ──────────────────────────────────────────────────────────────────────
 * Transparent no-show risk score explanation per
 * echo_overbooking_engine_spec.md §"View risk breakdown". Pure UI.
 *
 * Props:
 *   open, onOpenChange
 *   patient:  string
 *   time:     string
 *   provider: string
 *   score:    number (0..100)
 *   factors:  [{ label, detail, pts }]
 *   matches:  number (waitlist patients that match this slot)
 *   onOverbook?: fn — when "Overbook this slot" is clicked
 */
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../../ui/dialog';
import { Button } from '../../../ui/button';
import { Badge } from '../../../ui/badge';
import { cn } from '../../../../lib/utils';
import { Sparkles, AlertTriangle } from 'lucide-react';

const riskTier = (score) => {
  if (score >= 60) return { label: 'High risk', color: 'text-red-600', bg: 'bg-red-50 border-red-200' };
  if (score >= 30) return { label: 'Elevated', color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200' };
  return { label: 'Normal', color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200' };
};

const RiskBreakdownPanel = ({
  open, onOpenChange, patient, time, provider,
  score = 0, factors = [], matches = 0, onOverbook,
}) => {
  const tier = riskTier(score);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md p-0 gap-0 overflow-hidden"
        data-testid="risk-breakdown-panel"
      >
        <DialogHeader className="px-5 py-4 border-b border-slate-100">
          <DialogTitle className="text-base font-semibold">Risk score breakdown</DialogTitle>
          <DialogDescription className="text-[11px] text-slate-500">
            {patient} · {time} · {provider}
          </DialogDescription>
        </DialogHeader>
        <div className="p-5 space-y-4">
          <div className={cn('rounded-lg p-3 border flex items-center gap-3', tier.bg)}>
            <div className="flex flex-col">
              <span className={cn('text-2xl font-semibold', tier.color)} data-testid="risk-score">
                {score}%
              </span>
              <span className={cn('text-[11px] font-medium', tier.color)}>{tier.label}</span>
            </div>
            <div className="flex-1 text-[10px] text-slate-600">
              Echo scores every appointment nightly using patient history, lead time,
              confirmation, type, day/time, and insurance signals.
            </div>
          </div>

          <div className="space-y-1" data-testid="risk-factors">
            {factors.map((f, idx) => (
              <div
                key={idx}
                className="flex items-center gap-3 px-2 py-1.5 rounded border border-slate-100"
                data-testid={`risk-factor-${idx}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-slate-800 truncate">{f.label}</div>
                  <div className="text-[10px] text-slate-500 truncate">{f.detail}</div>
                </div>
                <span className="text-xs font-semibold text-slate-700 shrink-0">+{f.pts} pts</span>
              </div>
            ))}
          </div>

          <div className="rounded-lg border border-violet-200 bg-violet-50/60 p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <Sparkles className="h-3 w-3 text-violet-500" />
              <span className="text-[11px] font-semibold text-violet-800">
                Echo AI recommendation
              </span>
            </div>
            <p className="text-[11px] text-slate-700">
              {score >= 30 ? (
                <>
                  Overbook this slot. <b>{matches}</b> waitlist patient{matches === 1 ? '' : 's'} match.
                </>
              ) : (
                'No overbook action recommended. Continue standard reminders.'
              )}
            </p>
            {onOverbook && score >= 30 && (
              <Button
                size="sm"
                className="mt-2 h-7 text-[10px] bg-[#6b5ce7] hover:bg-[#5a4bd6] gap-1"
                onClick={() => { onOverbook(); onOpenChange?.(false); }}
                data-testid="risk-overbook-btn"
              >
                <Sparkles className="h-2.5 w-2.5" /> Overbook this slot
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RiskBreakdownPanel;
